import { useState, useCallback, useMemo } from "react";
import validator from "validator";
import { isPreviousFolioError } from "../utils/folders/createRecordError.ts";

export type TenantType = "fisica" | "moral";
export type GuarantorType = "fisica" | "moral";
export type LandlordType = "fisica" | "moral";

export interface FormData {
  // Step 1: Inquilino
  tenantType: TenantType;
  tenantName: string;
  tenantFirstSurname: string;
  tenantSecondSurname: string;
  tenantEmail: string;
  tenantPhoneNumber: string;
  tenantRfc: string;
  tenantCurp: string;

  // Contract renewal (MB-362) — applies to the whole expediente, captured in Step 1.
  isRenewal: boolean;
  // MB-362: folio of the previous contract being renewed (optional).
  previousFolio: string;

  // Step 2: Asesor
  advisorName: string;
  advisorFirstSurname: string;
  advisorSecondSurname: string;
  advisorEmail: string;
  advisorPhoneNumber: string;

  // Step 3: Fiador
  requiresGuarantor: boolean;
  guarantorType: GuarantorType;
  propertyAsGuarantee: boolean;
  guarantorEmail: string;

  // Step 4: Convenio y Representante
  alternativeJustice: boolean | null;
  landlordType: LandlordType;
  landlordEmail: string;
  requiresOwnerRepresentative: boolean | null;
}

export type FormErrors = Partial<Record<keyof FormData, string>>;

const initialFormData: FormData = {
  // Step 1: Inquilino
  tenantType: "fisica",
  tenantName: "",
  tenantFirstSurname: "",
  tenantSecondSurname: "",
  tenantEmail: "",
  tenantPhoneNumber: "",
  tenantRfc: "",
  tenantCurp: "",

  // Contract renewal (MB-362)
  isRenewal: false,
  previousFolio: "",

  // Step 2: Asesor
  advisorName: "",
  advisorFirstSurname: "",
  advisorSecondSurname: "",
  advisorEmail: "",
  advisorPhoneNumber: "",

  // Step 3: Fiador
  requiresGuarantor: false,
  guarantorType: "fisica",
  propertyAsGuarantee: false,
  guarantorEmail: "",

  // Step 4: Convenio y Representante
  alternativeJustice: null,
  landlordType: "fisica",
  landlordEmail: "",
  requiresOwnerRepresentative: null,
};

// Validation helpers with industry standards
const validateName = (name: string): string | null => {
  if (!name) return null;

  const trimmedName = name.trim();

  // Check for leading/trailing whitespace
  if (trimmedName !== name) {
    return "No se permiten espacios al inicio o final";
  }

  // Only letters (including accents), spaces, apostrophes, and hyphens
  const nameRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s'-]+$/;

  if (!nameRegex.test(trimmedName)) {
    return "Solo se permiten letras y espacios";
  }

  if (trimmedName.length < 2) {
    return "Mínimo 2 caracteres";
  }

  if (trimmedName.length > 50) {
    return "Máximo 50 caracteres";
  }

  // Check for multiple consecutive spaces
  if (/\s{2,}/.test(trimmedName)) {
    return "No se permiten espacios múltiples";
  }

  // Check for leading/trailing special characters (hyphen or apostrophe)
  if (/^[-']|[-']$/.test(trimmedName)) {
    return "No puede comenzar o terminar con guión o apóstrofe";
  }

  // Check for consecutive special characters
  if (/[-']{2,}/.test(trimmedName)) {
    return "No se permiten guiones o apóstrofes consecutivos";
  }

  return null;
};

const validateEmail = (email: string): string | null => {
  if (!email) return null;

  const trimmedEmail = email.trim();

  // Check for leading/trailing whitespace
  if (trimmedEmail !== email) {
    return "No se permiten espacios al inicio o final";
  }

  if (trimmedEmail.length > 100) {
    return "Máximo 100 caracteres";
  }

  // validator.isEmail already handles:
  // - Format validation (user@domain.tld)
  // - Consecutive dots
  // - Invalid characters
  // - Domain validation
  if (!validator.isEmail(trimmedEmail)) {
    return "Ingresa un correo válido";
  }

  return null;
};

const validatePhoneNumber = (phone: string): string | null => {
  if (!phone) return null;

  const phoneRegex = /^\d{10}$/;

  if (!phoneRegex.test(phone)) {
    return "Debe ser exactamente 10 dígitos";
  }

  // Check for invalid patterns:

  // All same digit (1111111111, 0000000000, etc.)
  if (/^(\d)\1{9}$/.test(phone)) {
    return "Número de teléfono inválido";
  }

  // Sequential ascending (0123456789, 1234567890)
  if (phone === "0123456789" || phone === "1234567890") {
    return "Número de teléfono inválido";
  }

  // Sequential descending (9876543210)
  if (phone === "9876543210") {
    return "Número de teléfono inválido";
  }

  return null;
};

const validateRFC = (rfc: string): string | null => {
  if (!rfc) return null;

  // Validate length first
  if (rfc.length !== 12 && rfc.length !== 13) {
    return "RFC debe tener 12 o 13 caracteres";
  }

  const personalRfcRegex = /^([A-ZÑ&]{4}([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1]))([A-Z\d]{2}([A]|[0-9]){1})?$/;
  const companyRfcRegex = /^[A-ZÑ&]{3}[0-9]{6}[A-Z0-9]{3}$/;

  // Check if it matches personal or company format
  const isPersonal = personalRfcRegex.test(rfc);
  const isCompany = companyRfcRegex.test(rfc);

  if (!isPersonal && !isCompany) {
    return "Formato de RFC inválido";
  }

  // Validate date for personal RFC (positions 4-9: YYMMDD)
  if (isPersonal) {
    const month = parseInt(rfc.substring(6, 8), 10);
    const day = parseInt(rfc.substring(8, 10), 10);

    // Validate month
    if (month < 1 || month > 12) {
      return "Fecha inválida en RFC (mes)";
    }

    // Validate day based on month
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]; // Feb allows 29 for leap years
    if (day < 1 || day > daysInMonth[month - 1]) {
      return "Fecha inválida en RFC (día)";
    }
  }

  // Validate date for company RFC (positions 3-8: YYMMDD)
  if (isCompany) {
    const month = parseInt(rfc.substring(5, 7), 10);
    const day = parseInt(rfc.substring(7, 9), 10);

    // Validate month
    if (month < 1 || month > 12) {
      return "Fecha inválida en RFC (mes)";
    }

    // Validate day based on month
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (day < 1 || day > daysInMonth[month - 1]) {
      return "Fecha inválida en RFC (día)";
    }
  }

  return null;
};

const validateCURP = (curp: string): string | null => {
  if (!curp) return null;

  if (curp.length !== 18) {
    return "CURP debe tener 18 caracteres";
  }

  const curpRegex = /^([A-Z][AEIOUX][A-Z]{2}[0-9]{2}(?:0[1-9]|1[012])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|B[CS]|C[CLMSH]|D[FG]|G[TR]|HG|JC|M[CNS]|N[ETL]|OC|PL|Q[TR]|S[PLR]|T[CSL]|VZ|YN|ZS)[B-DF-HJ-NP-TV-Z]{3}[A-Z\d]{1})([\d]{1})$/;

  if (!curpRegex.test(curp)) {
    return "Formato de CURP inválido";
  }

  // Validate date portion (positions 4-9: YYMMDD)
  const month = parseInt(curp.substring(6, 8), 10);
  const day = parseInt(curp.substring(8, 10), 10);

  // Validate month
  if (month < 1 || month > 12) {
    return "Fecha inválida en CURP (mes)";
  }

  // Validate day based on month
  const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]; // Feb allows 29 for leap years
  if (day < 1 || day > daysInMonth[month - 1]) {
    return "Fecha inválida en CURP (día)";
  }

  return null;
};

// Input sanitizers to prevent invalid characters
export const sanitizeName = (value: string): string => {
  // Remove any character that's not a letter, space, apostrophe, or hyphen
  // Allow accented characters and Ñ
  return value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s'-]/g, '');
};

export const sanitizeEmail = (value: string): string => {
  // Convert to lowercase and trim
  return value.toLowerCase().trim();
};

export const sanitizePhone = (value: string): string => {
  // Only digits, max 10
  return value.replace(/\D/g, '').slice(0, 10);
};

export const sanitizeRFC = (value: string): string => {
  // Only alphanumeric, &, and Ñ - uppercase
  return value.toUpperCase().replace(/[^A-Z0-9&Ñ]/g, '').slice(0, 13);
};

export const sanitizeCURP = (value: string): string => {
  // Only alphanumeric - uppercase, max 18
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 18);
};

export const useCreateRecordForm = () => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  // MB-379: why the last "Crear Expediente" failed. Shown inside the dialog (a toast
  // renders behind its overlay); any edit clears it, since the user is fixing it.
  const [submitError, setSubmitError] = useState<string | null>(null);

  const updateFormData = useCallback(
    <K extends keyof FormData>(key: K, value: FormData[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
      setSubmitError(null);
      // Clear error when user types
      if (errors[key]) {
        setErrors((prev) => ({ ...prev, [key]: undefined }));
      }
    },
    [errors]
  );

  const updateMultipleFormData = useCallback(
    (updates: Partial<FormData>) => {
      setFormData((prev) => ({ ...prev, ...updates }));
    },
    []
  );

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
    setSubmitError(null);
  }, []);

  // MB-379: keeps the form as it is so the user can fix it. When the backend blames the
  // previous folio (F008), the field itself carries the message too.
  const reportSubmitError = useCallback((message: string) => {
    setSubmitError(message);
    if (isPreviousFolioError(message)) {
      setErrors((prev) => ({ ...prev, previousFolio: message }));
    }
  }, []);

  // MB-379: a new attempt starts clean, so a repeated failure shows up (and is announced)
  // again, and a different failure doesn't leave the old F008 on the folio field (only
  // `reportSubmitError` ever sets that field's error).
  const clearSubmitError = useCallback(() => {
    setSubmitError(null);
    setErrors((prev) =>
      prev.previousFolio ? { ...prev, previousFolio: undefined } : prev,
    );
  }, []);

  const validateField = useCallback(
    (fieldName: keyof FormData): boolean => {
      let error: string | null = null;

      switch (fieldName) {
        // Tenant name fields
        case "tenantName":
        case "tenantFirstSurname":
          if (!formData[fieldName].trim()) {
            error = "Este campo es requerido";
          } else {
            error = validateName(formData[fieldName]);
          }
          break;
        case "tenantSecondSurname":
          if (formData.tenantSecondSurname.trim()) {
            error = validateName(formData.tenantSecondSurname);
          }
          break;
        case "tenantEmail":
          if (!formData.tenantEmail.trim()) {
            error = "Este campo es requerido";
          } else {
            error = validateEmail(formData.tenantEmail);
          }
          break;
        case "tenantPhoneNumber":
          if (!formData.tenantPhoneNumber.trim()) {
            error = "Este campo es requerido";
          } else {
            error = validatePhoneNumber(formData.tenantPhoneNumber);
          }
          break;
        case "tenantRfc":
          error = validateRFC(formData.tenantRfc);
          break;
        case "tenantCurp":
          error = validateCURP(formData.tenantCurp);
          break;

        // Advisor name fields
        case "advisorName":
        case "advisorFirstSurname":
          if (!formData[fieldName].trim()) {
            error = "Este campo es requerido";
          } else {
            error = validateName(formData[fieldName]);
          }
          break;
        case "advisorSecondSurname":
          if (formData.advisorSecondSurname.trim()) {
            error = validateName(formData.advisorSecondSurname);
          }
          break;
        case "advisorEmail":
          if (!formData.advisorEmail.trim()) {
            error = "Este campo es requerido";
          } else {
            error = validateEmail(formData.advisorEmail);
          }
          break;
        case "advisorPhoneNumber":
          if (!formData.advisorPhoneNumber.trim()) {
            error = "Este campo es requerido";
          } else {
            error = validatePhoneNumber(formData.advisorPhoneNumber);
          }
          break;

        // Optional email fields
        case "guarantorEmail":
          if (formData.guarantorEmail.trim()) {
            error = validateEmail(formData.guarantorEmail);
          }
          break;
        case "landlordEmail":
          if (formData.landlordEmail.trim()) {
            error = validateEmail(formData.landlordEmail);
          }
          break;
      }

      if (error) {
        setErrors((prev) => ({ ...prev, [fieldName]: error }));
        return false;
      } else {
        setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
        return true;
      }
    },
    [formData]
  );

  // Auto-select templates based on form data
  const selectedTemplates = useMemo(() => {
    const templates: string[] = [];

    // Step 1: Inquilino template
    if (formData.tenantType === "fisica") {
      templates.push("tenant-pf-cja");
    } else {
      templates.push("tenant-pm-cja");
    }

    // Step 3: Fiador templates
    if (formData.requiresGuarantor) {
      if (formData.guarantorType === "fisica") {
        templates.push("guarantor-pf-cja");
      } else {
        templates.push("guarantor-pm-cja");
      }

      if (formData.propertyAsGuarantee) {
        templates.push("collateral");
      }
    }

    // Step 4: Convenio de Justicia Alternativa
    if (formData.alternativeJustice) {
      if (formData.landlordType === "fisica") {
        templates.push("lease-contract-pf");
      } else {
        templates.push("lease-contract-pm");
      }
    }

    // Step 4: Representante del propietario
    if (formData.requiresOwnerRepresentative) {
      templates.push("owner-representative");
    }

    return templates;
  }, [
    formData.tenantType,
    formData.requiresGuarantor,
    formData.guarantorType,
    formData.propertyAsGuarantee,
    formData.alternativeJustice,
    formData.landlordType,
    formData.requiresOwnerRepresentative,
  ]);

  return {
    formData,
    errors,
    updateFormData,
    updateMultipleFormData,
    resetForm,
    validateField,
    selectedTemplates,
    submitError,
    reportSubmitError,
    clearSubmitError,
  };
};
